import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, MoreVertical, Edit, Trash2, UserCheck, UserX, Eye, EyeOff, RefreshCw, Building, ExternalLink, Settings, Sliders } from 'lucide-react';
import { subAdminsAPI } from '../../services/api';

const AdminSubAdmins = () => {
  const navigate = useNavigate();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionMenu, setActionMenu] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSubAdmin, setEditingSubAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    user_limit: 10
  });

  useEffect(() => {
    fetchSubAdmins();
  }, []);

  const fetchSubAdmins = async () => {
    setLoading(true);
    try {
      const response = await subAdminsAPI.getAll();
      setSubAdmins(response.data.sub_admins || []);
    } catch (err) {
      console.error('Error fetching sub-admins:', err);
      setError('Failed to fetch sub-admins');
    } finally {
      setLoading(false);
    }
  };

  const filteredSubAdmins = subAdmins.filter(sa =>
    sa.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sa.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sa.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateSubAdmin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await subAdminsAPI.create({
        ...formData,
        user_limit: parseInt(formData.user_limit) || 10
      });
      setSuccess('Client created successfully! They can now log in at /client');
      setShowModal(false);
      setFormData({ name: '', email: '', password: '', company_name: '', user_limit: 10 });
      fetchSubAdmins();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Error creating sub-admin:', err);
      setError(err.response?.data?.detail || 'Failed to create sub-admin');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubAdmin = async (e) => {
    e.preventDefault();
    if (!editingSubAdmin) return;
    
    setLoading(true);
    try {
      const updateData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.email) updateData.email = formData.email;
      if (formData.password) updateData.password = formData.password;
      if (formData.company_name) updateData.company_name = formData.company_name;
      if (formData.user_limit !== undefined) updateData.user_limit = parseInt(formData.user_limit) || 10;
      
      await subAdminsAPI.update(editingSubAdmin.id, updateData);
      setSuccess('Client updated successfully');
      setShowEditModal(false);
      setEditingSubAdmin(null);
      setFormData({ name: '', email: '', password: '', company_name: '', user_limit: 10 });
      fetchSubAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating sub-admin:', err);
      setError(err.response?.data?.detail || 'Failed to update sub-admin');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (subAdminId, newStatus) => {
    setLoading(true);
    try {
      await subAdminsAPI.update(subAdminId, { status: newStatus });
      setSuccess(`Client ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
      fetchSubAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update sub-admin status');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const handleDelete = async (subAdminId) => {
    if (!window.confirm('Are you sure you want to delete this client? This will also delete all their users and agents.')) return;
    
    setLoading(true);
    try {
      await subAdminsAPI.delete(subAdminId);
      setSuccess('Client deleted successfully');
      fetchSubAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting sub-admin:', err);
      setError('Failed to delete sub-admin');
    } finally {
      setLoading(false);
      setActionMenu(null);
    }
  };

  const openEditModal = (subAdmin) => {
    setEditingSubAdmin(subAdmin);
    setFormData({
      name: subAdmin.name,
      email: subAdmin.email,
      password: '',
      company_name: subAdmin.company_name || '',
      user_limit: subAdmin.user_limit || 10
    });
    setShowEditModal(true);
    setActionMenu(null);
  };

  const copyLoginUrl = () => {
    const url = `${window.location.origin}/client`;
    navigator.clipboard.writeText(url);
    setSuccess('Client portal URL copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="p-8" data-testid="admin-subadmins-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Client Management</h1>
          <p className="text-gray-400">Create and manage client accounts who can manage their own users and AI voice agents</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={copyLoginUrl}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            Copy Client Portal URL
          </button>
          <button
            onClick={fetchSubAdmins}
            disabled={loading}
            className="flex items-center gap-2 border border-gray-700 text-gray-300 hover:bg-gray-800 px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => {
              setFormData({ name: '', email: '', password: '', company_name: '', user_limit: 10 });
              setShowModal(true);
            }}
            className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors"
            data-testid="add-subadmin-btn"
          >
            <Plus className="w-5 h-5" />
            Add Client
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Building className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Client Portal</p>
            <p className="text-gray-400 text-sm">Clients can log in at <span className="text-blue-400">/client</span> to manage their users and AI voice agents with scripts.</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
          {error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-300">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400">
          {success}
        </div>
      )}

      {/* Search */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            data-testid="search-subadmins-input"
          />
        </div>
      </div>

      {/* Sub-Admins Table */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a1a1f]">
              <tr>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">CLIENT</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">COMPANY</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">USER LIMIT</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">CREATED</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">STATUS</th>
                <th className="text-left text-gray-400 text-xs font-medium px-6 py-4">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubAdmins.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    {loading ? 'Loading sub-admins...' : 'No sub-admins found. Click "Add Sub-Admin" to create one.'}
                  </td>
                </tr>
              ) : (
                filteredSubAdmins.map((subAdmin) => (
                  <tr key={subAdmin.id} className="border-t border-gray-800 hover:bg-[#1a1a1f]/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold">
                          {subAdmin.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{subAdmin.name}</p>
                          <p className="text-gray-500 text-sm">{subAdmin.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{subAdmin.company_name || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-blue-400/10 text-blue-400 rounded-full text-xs font-medium">
                        {subAdmin.user_limit || 10} users
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {subAdmin.created_at ? new Date(subAdmin.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        subAdmin.status === 'active' ? 'bg-green-400/10 text-green-400' :
                        subAdmin.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' :
                        'bg-red-400/10 text-red-400'
                      }`}>
                        {subAdmin.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <button
                          onClick={() => setActionMenu(actionMenu === subAdmin.id ? null : subAdmin.id)}
                          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                          data-testid={`subadmin-action-menu-${subAdmin.id}`}
                        >
                          <MoreVertical className="w-5 h-5 text-gray-400" />
                        </button>
                        {actionMenu === subAdmin.id && (
                          <div className="absolute right-0 top-full mt-1 w-56 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-10">
                            <button
                              onClick={() => openEditModal(subAdmin)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              Quick Edit
                            </button>
                            <button
                              onClick={() => {
                                setActionMenu(null);
                                navigate(`/admin/clients/${subAdmin.id}/settings`);
                              }}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-teal-400 hover:bg-gray-800 transition-colors"
                            >
                              <Sliders className="w-4 h-4" />
                              Full Settings & Controls
                            </button>
                            <div className="border-t border-gray-700 my-1"></div>
                            {subAdmin.status === 'active' ? (
                              <button
                                onClick={() => handleStatusChange(subAdmin.id, 'suspended')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-yellow-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserX className="w-4 h-4" />
                                Suspend
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(subAdmin.id, 'active')}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-green-400 hover:bg-gray-800 transition-colors"
                              >
                                <UserCheck className="w-4 h-4" />
                                Activate
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(subAdmin.id)}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-gray-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Client Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-6">Create New Client</h3>
            <form onSubmit={handleCreateSubAdmin} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter full name"
                  required
                  data-testid="create-subadmin-name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter email address"
                  required
                  data-testid="create-subadmin-email"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter password (min 6 characters)"
                    required
                    minLength={6}
                    data-testid="create-subadmin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Company Name (Optional)</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter company name"
                  data-testid="create-subadmin-company"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">User Limit *</label>
                <input
                  type="number"
                  value={formData.user_limit}
                  onChange={(e) => setFormData({...formData, user_limit: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Maximum number of users"
                  min="1"
                  max="1000"
                  required
                  data-testid="create-subadmin-userlimit"
                />
                <p className="text-gray-500 text-xs mt-1">Max users this client can create</p>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="create-subadmin-submit"
                >
                  {loading ? 'Creating...' : 'Create Sub-Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editingSubAdmin && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-6">Edit Client</h3>
            <form onSubmit={handleUpdateSubAdmin} className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">New Password (leave blank to keep current)</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-12 text-white outline-none focus:border-teal-400"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">Company Name</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-2">User Limit</label>
                <input
                  type="number"
                  value={formData.user_limit}
                  onChange={(e) => setFormData({...formData, user_limit: e.target.value})}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="Maximum number of users"
                  min="1"
                  max="1000"
                />
                <p className="text-gray-500 text-xs mt-1">Max users this client can create</p>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSubAdmin(null);
                  }}
                  className="flex-1 border border-gray-700 text-gray-300 py-2.5 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-400 hover:bg-teal-500 disabled:bg-teal-400/50 text-black py-2.5 rounded-lg font-medium transition-colors"
                >
                  {loading ? 'Updating...' : 'Update Sub-Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubAdmins;
